// @ts-check
// Aba "Retenção" de Configurações > Atendimento (contrato 10 de atendimento, seções 19.2 e 24.4): pesos dos sinais,
// limite, validade, dias sem resposta à rematrícula, abrir caso sozinho, regra do dono, departamento dos casos e
// cadência. Montada por ServiceDeskSettingsPage (F3). Quem não configura o atendimento vê só a leitura.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { fetchRetentionSettings, saveRetentionSettings } from '../../data/retention.js';
import { findManyRecords } from '../../data/rest.js';
import { formatCadenceText, isSettingNumberValid, parseCadenceText, settingsPatch, weightRows } from '../../lib/retention-display.js';
import { RETENTION_OWNER_RULES } from '../../../../shared/retention/retention-settings.js';
import { Button } from '../../ui/button.js';
import { Skeleton, Toggle } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { showServiceError } from '../service-desk/service-errors.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../../../shared/retention/retention-settings.js').RetentionSettings} RetentionSettings */

/** @param {{ id: string, label: string, hint?: string, error?: string | null, children: any }} props */
function Field({ id, label, hint, error, children }) {
  return html`<div class="retention-form-field">
    <label class="retention-field-label" for=${id}>${label}</label>
    ${children}
    ${error ? html`<span class="retention-problem" role="alert">${error}</span>` : hint ? html`<span class="retention-muted">${hint}</span>` : null}
  </div>`;
}

/** @param {{ canEdit: boolean }} props */
export function RetentionSettingsTab({ canEdit }) {
  const [saved, setSaved] = useState(/** @type {RetentionSettings | null} */ (null));
  const [draft, setDraft] = useState(/** @type {RetentionSettings | null} */ (null));
  const [cadenceText, setCadenceText] = useState('');
  const [teams, setTeams] = useState(/** @type {Array<{ id: string, name: string }>} */ ([]));
  const [canUpdate, setCanUpdate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    fetchRetentionSettings().then((loaded) => {
      setSaved(loaded.settings);
      setDraft(loaded.settings);
      setCadenceText(formatCadenceText(loaded.settings.cadenceDays));
      setCanUpdate(loaded.canUpdate);
    }).catch(showErrorSnackbar);
    const teamObject = objectsByNameSingular.value.get('serviceTeam');
    if (teamObject) {
      findManyRecords(teamObject, { limit: 100 }).then(({ records }) => setTeams(records
        .filter((record) => (record.teamKind ?? 'STANDARD') === 'STANDARD' && record.isActive !== false).map((record) => ({ id: record.id, name: record.name ?? '' })))).catch(() => setTeams([]));
    }
  }, []);
  if (!saved || !draft) return html`<div class="retention-settings" aria-busy="true"><${Skeleton} width="100%" height=${240} /></div>`;
  const isEditable = canEdit && canUpdate;
  const cadence = parseCadenceText(cadenceText);
  const numberProblem = (/** @type {'threshold' | 'signalLifetimeDays' | 'noReenrollmentResponseDays'} */ key) => (isSettingNumberValid(key, draft[key]) ? null : t('retention.settings.invalidNumber'));
  const weightsValid = weightRows(draft.weights).every((row) => isSettingNumberValid('weight', row.weight));
  const next = cadence.days ? { ...draft, cadenceDays: cadence.days } : draft;
  const patch = settingsPatch(saved, next);
  const isDirty = Object.keys(patch).length > 0 || (cadence.problem !== null && cadenceText !== formatCadenceText(saved.cadenceDays));
  const isValid = !cadence.problem && weightsValid && !numberProblem('threshold') && !numberProblem('signalLifetimeDays') && !numberProblem('noReenrollmentResponseDays');
  /** @param {Partial<RetentionSettings>} changes */
  const update = (changes) => setDraft({ ...draft, ...changes });
  /** @param {string} value */
  const toInteger = (value) => (value.trim() === '' ? Number.NaN : Number(value));
  const save = async () => {
    setIsSaving(true);
    try {
      const settings = await saveRetentionSettings(patch);
      setSaved(settings);
      setDraft(settings);
      setCadenceText(formatCadenceText(settings.cadenceDays));
      showSuccessSnackbar(t('retention.settings.saved'));
    } catch (error) {
      showServiceError(error);
    } finally {
      setIsSaving(false);
    }
  };
  const discard = () => {
    setDraft(saved);
    setCadenceText(formatCadenceText(saved.cadenceDays));
  };

  return html`<div class="retention-settings" data-testid="retention-settings">
    <div class="retention-settings-intro">
      <h2 class="retention-settings-title">${t('retention.settings.title')}</h2>
      <p class="retention-muted">${t('retention.settings.description')}</p>
      ${isEditable ? null : html`<p class="retention-law-notice" role="note">${t('retention.settings.readOnly')}</p>`}
    </div>
    <section class="retention-settings-block">
      <h3 class="retention-subtitle">${t('retention.settings.weightsTitle')}</h3>
      <p class="retention-muted">${t('retention.settings.weightsHint')}</p>
      <div class="retention-weights">
        ${weightRows(draft.weights).map((row) => html`<label key=${row.key} class="retention-weight" data-off=${row.isOff ? 'true' : 'false'}>
          <span>${t(`retention.signals.${row.key}`)}</span>
          <input type="number" min="0" max="100" step="5" class="retention-input retention-weight-input" value=${Number.isNaN(row.weight) ? '' : row.weight} disabled=${!isEditable}
            aria-invalid=${isSettingNumberValid('weight', row.weight) ? 'false' : 'true'}
            onInput=${(/** @type {any} */ event) => update({ weights: { ...draft.weights, [row.key]: toInteger(event.currentTarget.value) } })} />
        </label>`)}
      </div>
    </section>
    <section class="retention-settings-block">
      <h3 class="retention-subtitle">${t('retention.settings.thresholdTitle')}</h3>
      <div class="retention-form-grid">
        <${Field} id="retention-threshold" label=${t('retention.settings.threshold')} hint=${t('retention.settings.thresholdHint')} error=${numberProblem('threshold')}>
          <input id="retention-threshold" type="number" min="10" max="100" class="retention-input" value=${Number.isNaN(draft.threshold) ? '' : draft.threshold} disabled=${!isEditable}
            onInput=${(/** @type {any} */ event) => update({ threshold: toInteger(event.currentTarget.value) })} />
        <//>
        <${Field} id="retention-lifetime" label=${t('retention.settings.lifetime')} hint=${t('retention.settings.lifetimeHint')} error=${numberProblem('signalLifetimeDays')}>
          <input id="retention-lifetime" type="number" min="7" max="365" class="retention-input" value=${Number.isNaN(draft.signalLifetimeDays) ? '' : draft.signalLifetimeDays} disabled=${!isEditable}
            onInput=${(/** @type {any} */ event) => update({ signalLifetimeDays: toInteger(event.currentTarget.value) })} />
        <//>
        <${Field} id="retention-no-response" label=${t('retention.settings.noResponseDays')} hint=${t('retention.settings.noResponseDaysHint')} error=${numberProblem('noReenrollmentResponseDays')}>
          <input id="retention-no-response" type="number" min="1" max="90" class="retention-input" value=${Number.isNaN(draft.noReenrollmentResponseDays) ? '' : draft.noReenrollmentResponseDays} disabled=${!isEditable}
            onInput=${(/** @type {any} */ event) => update({ noReenrollmentResponseDays: toInteger(event.currentTarget.value) })} />
        <//>
      </div>
    </section>
    <section class="retention-settings-block">
      <h3 class="retention-subtitle">${t('retention.settings.caseTitle')}</h3>
      <div class="retention-toggle-row">
        <${Toggle} value=${draft.autoOpenCase} disabled=${!isEditable} ariaLabel=${t('retention.settings.autoOpenCase')} onChange=${(/** @type {boolean} */ value) => update({ autoOpenCase: value })} />
        <span>${t('retention.settings.autoOpenCase')}</span>
      </div>
      <div class="retention-form-grid">
        <${Field} id="retention-owner-rule" label=${t('retention.settings.ownerRule')} hint=${t('retention.settings.ownerRuleHint')}>
          <select id="retention-owner-rule" class="retention-select" value=${draft.ownerRule} disabled=${!isEditable}
            onChange=${(/** @type {any} */ event) => update({ ownerRule: event.currentTarget.value })}>
            ${RETENTION_OWNER_RULES.map((rule) => html`<option key=${rule} value=${rule}>${t(`retention.settings.ownerRules.${rule}`)}</option>`)}
          </select>
        <//>
        <${Field} id="retention-owner-team" label=${t('retention.settings.ownerTeam')}>
          <select id="retention-owner-team" class="retention-select" value=${draft.ownerTeamId ?? ''} disabled=${!isEditable}
            onChange=${(/** @type {any} */ event) => update({ ownerTeamId: event.currentTarget.value || null })}>
            <option value="">${t('retention.settings.noTeam')}</option>
            ${teams.map((team) => html`<option key=${team.id} value=${team.id}>${team.name}</option>`)}
          </select>
        <//>
        <${Field} id="retention-cadence" label=${t('retention.settings.cadence')} hint=${t('retention.settings.cadenceHint')}
          error=${cadence.problem ? t(`retention.settings.problems.${cadence.problem}`) : null}>
          <input id="retention-cadence" type="text" inputmode="numeric" class="retention-input" value=${cadenceText} disabled=${!isEditable}
            onInput=${(/** @type {any} */ event) => setCadenceText(event.currentTarget.value)} />
        <//>
      </div>
    </section>
    ${isEditable
      ? html`<div class="retention-settings-actions">
          <${Button} title=${t('retention.settings.discard')} variant="secondary" size="small" disabled=${!isDirty || isSaving} onClick=${discard} />
          <${Button} title=${t('retention.settings.save')} accent="blue" size="small" disabled=${!isDirty || !isValid} isLoading=${isSaving} onClick=${save} />
        </div>`
      : null}
  </div>`;
}
